"use client";

import React, { useState, useEffect, useCallback } from "react";
import { HeaderSection } from "@/components/shared/header-section";
import { PageContainer } from "@/components/shared/page-container";
import { useLazyQuery } from "@apollo/client";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { columns } from "./components/columns";
import { DataTableToolbar } from "@/components/shared/data-table-manual/data-table-toolbar-manual";
import { useDrawerDispatch } from "@/context/DrawerContext";
import { Button } from "@/components/ui/button";
import { DELETE_MANY_TAGS, GET_TAGS } from "./tagsQueries";
import TagForm from "@/containers/Tags/components/TagForm";
import useOpenDrawer from "@/hooks/useOpenDrawer";
import { useDeleteConfirmation } from "@/hooks/useDeleteConfirmation";
import { PageContent } from "@/components/shared/page-content";
import { getOrderSortParams } from "@/utils/orderUtils";

export default function Tags() {
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState([]);
  const [tableDataCount, setTableDataCount] = useState(0);
  const [order, setOrder] = useState([]);
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  );

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [loadTags, { data, error, loading }] = useLazyQuery(GET_TAGS, {
    variables: {},
    fetchPolicy: "cache-and-network",
  });

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_TAGS,
    refetchQueries: ["GET_TAGS"],
    messages: {
      success: "Tags deleted successfully",
      error: "Failed to delete tags",
    },
    itemName: "tag",
  });

  // Load tags data
  useEffect(() => {
    loadTags({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    });
  }, [searchTerm, pagination, order]);

  // Reset to first page when search or page size changes
  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 });
  }, [searchTerm, pagination.pageSize, order]);

  // Update table data when query results change
  useEffect(() => {
    if (data) {
      setTableData(data.findManyTag);
      setTableDataCount(data.findManyTagCount ?? 0);
    }
  }, [data]);

  const openTagForm = useOpenDrawer(TagForm);

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Tags" />
        <Button onClick={openTagForm}>Create New +</Button>
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={openTagForm}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: "Filter tags...",
                accessor: "name",
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [], // Add empty columns array to satisfy type requirement
            }}
          />
        )}
      />
    </PageContent>
  );
}
